import { useEffect, useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { DirectMessage, Conversation, Profile, Course } from '../../types/database';
import { Send, MessageSquare, Bell, Search, Check, CheckCheck, X, Paperclip, Image as ImageIcon, Users } from 'lucide-react';
import { toast } from '../ui/Toaster';
import { Spinner } from '../ui/Spinner';

interface ConversationWithUser extends Conversation {
  other_user: Profile;
  unread_count: number;
  last_message?: string;
}

interface DirectMessageWithSender extends DirectMessage {
  sender: Profile;
}

interface GroupMessageFormProps {
  courses: Course[];
  onSend: (courseId: string, message: string) => Promise<void>;
  onCancel: () => void;
}

function GroupMessageForm({ courses, onSend, onCancel }: GroupMessageFormProps) {
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [message, setMessage] = useState('');
  const [studentCount, setStudentCount] = useState<number>(0);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (selectedCourse) {
      loadStudentCount();
    }
  }, [selectedCourse]);

  const loadStudentCount = async () => {
    try {
      const { count } = await supabase
        .from('course_enrollments')
        .select('*', { count: 'exact', head: true })
        .eq('course_id', selectedCourse)
        .eq('status', 'active');

      setStudentCount(count || 0);
    } catch (error) {
      console.error('Error loading student count:', error);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourse || !message.trim() || sending) return;

    setSending(true);
    try {
      await onSend(selectedCourse, message.trim());
      setSelectedCourse('');
      setMessage('');
    } catch (error) {
      console.error('Error sending group message:', error);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSend} className="space-y-4">
      <button
        type="button"
        onClick={onCancel}
        className="mb-2 text-sm text-muted hover:text-white transition-colors flex items-center gap-2"
      >
        <X className="w-4 h-4" />
        Volver
      </button>

      <div>
        <label className="block text-sm font-medium text-white/70 mb-2">
          Seleccionar curso
        </label>
        <select
          value={selectedCourse}
          onChange={(e) => setSelectedCourse(e.target.value)}
          className="w-full px-4 py-2.5 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
          required
        >
          <option value="">Selecciona un curso...</option>
          {courses.map((course) => (
            <option key={course.id} value={course.id}>
              {course.name}
            </option>
          ))}
        </select>
        {selectedCourse && (
          <p className="mt-2 text-sm text-muted flex items-center gap-2">
            <Users className="w-4 h-4" />
            {studentCount} alumno(s) recibirán este mensaje
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-white/70 mb-2">
          Mensaje
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Escribe tu mensaje de bienvenida o anuncio..."
          rows={6}
          className="w-full px-4 py-3 glass-deep border border-white/[0.08] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cubus-lime resize-none"
          required
        />
        <p className="mt-1 text-xs text-subtle">
          Este mensaje se enviará individualmente a cada alumno del curso
        </p>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 glass-deep hover:bg-white/[0.06] text-white rounded-lg transition-all border border-white/[0.08]"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!selectedCourse || !message.trim() || sending}
          className="flex-1 px-4 py-2.5 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {sending ? (
            <>
              <div className="w-4 h-4 border-2 border-white/[0.08] border-t-transparent rounded-full animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Enviar a todos
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export function Chat() {
  const { profile } = useAuth();
  const [conversations, setConversations] = useState<ConversationWithUser[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ConversationWithUser | null>(null);
  const [messages, setMessages] = useState<DirectMessageWithSender[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [availableUsers, setAvailableUsers] = useState<Profile[]>([]);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [teacherCourses, setTeacherCourses] = useState<Course[]>([]);
  const [modalView, setModalView] = useState<'menu' | 'individual' | 'group' | 'broadcast'>('menu');
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [broadcastRecipients, setBroadcastRecipients] = useState<Set<string>>(new Set());
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile) {
      loadConversations();
      loadAvailableUsers();
      subscribeToNewMessages();
      if (profile.role === 'docente') {
        loadTeacherCourses();
      }
    }
    // Dep en profile?.id (string) y no en profile (objeto), así no recargamos
    // toda la lista de conversaciones cada vez que Supabase refresca el token
    // y AuthContext reemplaza el objeto profile por uno con misma data pero
    // distinta referencia (ocurre al volver al tab desde otra ventana).
  }, [profile?.id, profile?.role]);

  useEffect(() => {
    if (conversations.length > 0 && !selectedConversation) {
      setSelectedConversation(conversations[0]);
    }
  }, [conversations]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages();
      markMessagesAsRead();
      subscribeToConversationMessages();
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadTeacherCourses = async () => {
    if (!profile || profile.role !== 'docente') return;

    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .or(`docente_id.eq.${profile.id},secondary_docente_id.eq.${profile.id}`)
        .eq('status', 'active')
        .order('name', { ascending: true });

      if (error) throw error;
      setTeacherCourses(data || []);
    } catch (error) {
      console.error('Error loading teacher courses:', error);
      setTeacherCourses([]);
    }
  };

  const loadAvailableUsers = async () => {
    if (!profile) return;

    try {
      if (profile.role === 'alumno') {
        const { data: enrollments } = await supabase
          .from('course_enrollments')
          .select('course_id, courses!inner(status)')
          .eq('alumno_id', profile.id)
          .eq('status', 'active')
          .eq('courses.status', 'active');

        const courseIds = enrollments?.map(e => e.course_id) || [];

        if (courseIds.length > 0) {
          const { data: courses } = await supabase
            .from('courses')
            .select('docente_id, secondary_docente_id')
            .in('id', courseIds)
            .not('docente_id', 'is', null);

          const docenteIds = [...new Set(
            courses?.flatMap(c => [c.docente_id, c.secondary_docente_id].filter(Boolean)) || []
          )];

          if (docenteIds.length > 0) {
            const { data, error } = await supabase
              .from('profiles')
              .select('*')
              .in('id', docenteIds)
              .eq('active', true);

            if (error) throw error;
            setAvailableUsers(data || []);
            return;
          }
        }
        setAvailableUsers([]);
      } else if (profile.role === 'docente') {
        const { data: courses } = await supabase
          .from('courses')
          .select('id')
          .or(`docente_id.eq.${profile.id},secondary_docente_id.eq.${profile.id}`);

        const courseIds = courses?.map(c => c.id) || [];

        if (courseIds.length > 0) {
          const { data: enrollments } = await supabase
            .from('course_enrollments')
            .select('alumno_id')
            .in('course_id', courseIds)
            .eq('status', 'active');

          const alumnoIds = [...new Set(enrollments?.map(e => e.alumno_id))] || [];

          if (alumnoIds.length > 0) {
            const { data, error } = await supabase
              .from('profiles')
              .select('*')
              .in('id', alumnoIds)
              .eq('active', true);

            if (error) throw error;
            setAvailableUsers(data || []);
            return;
          }
        }
        setAvailableUsers([]);
      } else {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('active', true)
          .neq('id', profile.id);

        if (error) throw error;
        setAvailableUsers(data || []);
      }
    } catch (error) {
      console.error('Error loading available users:', error);
      setAvailableUsers([]);
    }
  };

  const loadConversations = async () => {
    if (!profile) return;

    try {
      const { data: convData, error } = await supabase
        .from('conversations')
        .select('*')
        .or(`user1_id.eq.${profile.id},user2_id.eq.${profile.id}`)
        .order('last_message_at', { ascending: false });

      if (error) throw error;

      const conversationsWithUsers = await Promise.all(
        (convData || []).map(async (conv) => {
          const otherUserId = conv.user1_id === profile.id ? conv.user2_id : conv.user1_id;

          const { data: userData } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', otherUserId)
            .single();

          const { data: lastMsg } = await supabase
            .from('direct_messages')
            .select('content')
            .eq('conversation_id', conv.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          const { count } = await supabase
            .from('direct_messages')
            .select('*', { count: 'exact', head: true })
            .eq('conversation_id', conv.id)
            .eq('receiver_id', profile.id)
            .eq('read', false);

          return {
            ...conv,
            other_user: userData!,
            unread_count: count || 0,
            last_message: lastMsg?.content
          };
        })
      );

      setConversations(conversationsWithUsers);

      const total = conversationsWithUsers.reduce((sum, conv) => sum + conv.unread_count, 0);
      setTotalUnreadCount(total);
      setLoading(false);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!selectedConversation) return;

    try {
      const { data, error } = await supabase
        .from('direct_messages')
        .select(`
          *,
          sender:sender_id(*)
        `)
        .eq('conversation_id', selectedConversation.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data as any || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const markMessagesAsRead = async () => {
    if (!selectedConversation || !profile) return;

    try {
      await supabase
        .from('direct_messages')
        .update({ read: true })
        .eq('conversation_id', selectedConversation.id)
        .eq('receiver_id', profile.id)
        .eq('read', false);

      loadConversations();
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const subscribeToNewMessages = () => {
    if (!profile) return;

    const channel = supabase
      .channel('new-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'direct_messages',
          filter: `receiver_id=eq.${profile.id}`,
        },
        () => {
          loadConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const subscribeToConversationMessages = () => {
    if (!selectedConversation) return;

    const channel = supabase
      .channel(`conversation-${selectedConversation.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'direct_messages',
          filter: `conversation_id=eq.${selectedConversation.id}`,
        },
        async (payload) => {
          if (payload.new.sender_id === profile?.id) {
            return;
          }

          const { data: senderData } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', payload.new.sender_id)
            .single();

          const newMsg: DirectMessageWithSender = {
            ...(payload.new as DirectMessage),
            sender: senderData!,
          };

          setMessages((prev) => {
            const exists = prev.some(m => m.id === newMsg.id);
            if (exists) return prev;
            return [...prev, newMsg];
          });

          if (payload.new.receiver_id === profile?.id) {
            markMessagesAsRead();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const startNewConversation = async (otherUser: Profile) => {
    if (!profile) return;

    try {
      const user1Id = profile.id < otherUser.id ? profile.id : otherUser.id;
      const user2Id = profile.id < otherUser.id ? otherUser.id : profile.id;

      const { data: existingConv } = await supabase
        .from('conversations')
        .select('*')
        .eq('user1_id', user1Id)
        .eq('user2_id', user2Id)
        .maybeSingle();

      if (existingConv) {
        const { data: userData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', otherUser.id)
          .single();

        const { data: lastMsg } = await supabase
          .from('direct_messages')
          .select('content')
          .eq('conversation_id', existingConv.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        const { count } = await supabase
          .from('direct_messages')
          .select('*', { count: 'exact', head: true })
          .eq('conversation_id', existingConv.id)
          .eq('receiver_id', profile.id)
          .eq('read', false);

        const convWithUser: ConversationWithUser = {
          ...existingConv,
          other_user: userData!,
          unread_count: count || 0,
          last_message: lastMsg?.content
        };

        setSelectedConversation(convWithUser);

        const existingIndex = conversations.findIndex(c => c.id === existingConv.id);
        if (existingIndex === -1) {
          setConversations([convWithUser, ...conversations]);
        }
      } else {
        const { data: newConv, error } = await supabase
          .from('conversations')
          .insert({
            user1_id: user1Id,
            user2_id: user2Id,
          })
          .select()
          .single();

        if (error) throw error;

        const newConvWithUser: ConversationWithUser = {
          ...newConv,
          other_user: otherUser,
          unread_count: 0,
        };

        setConversations([newConvWithUser, ...conversations]);
        setSelectedConversation(newConvWithUser);
      }

      setShowNewChatModal(false);
      setModalView('menu');
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  const sendMessageToAllStudents = async (courseId: string, message: string) => {
    if (!profile || !message.trim()) return;

    try {
      const { data: enrollments, error: enrollError } = await supabase
        .from('course_enrollments')
        .select('alumno_id')
        .eq('course_id', courseId)
        .eq('status', 'active');

      if (enrollError) throw enrollError;

      const studentIds = [...new Set(enrollments?.map(e => e.alumno_id))] || [];

      for (const studentId of studentIds) {
        const user1Id = profile.id < studentId ? profile.id : studentId;
        const user2Id = profile.id < studentId ? studentId : profile.id;

        let conversationId: string;

        const { data: existingConv } = await supabase
          .from('conversations')
          .select('id')
          .eq('user1_id', user1Id)
          .eq('user2_id', user2Id)
          .maybeSingle();

        if (existingConv) {
          conversationId = existingConv.id;
        } else {
          const { data: newConv, error: convError } = await supabase
            .from('conversations')
            .insert({
              user1_id: user1Id,
              user2_id: user2Id,
            })
            .select('id')
            .single();

          if (convError) throw convError;
          conversationId = newConv.id;
        }

        await supabase
          .from('direct_messages')
          .insert({
            conversation_id: conversationId,
            sender_id: profile.id,
            receiver_id: studentId,
            content: message,
          });
      }

      loadConversations();
      setShowNewChatModal(false);
      setModalView('menu');
      toast.success(`Mensaje enviado a ${studentIds.length} alumno(s) del curso`);
    } catch (error) {
      console.error('Error sending message to all students:', error);
      toast.error('Error al enviar el mensaje');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const allowed = ['.png', '.pdf', '.rvt', '.rfa', '.rte', '.jpeg', '.jpg', '.dwg', '.docx', '.xlsx', '.pptx', '.zip', '.rar'];
    const maxSize = 50 * 1024 * 1024;
    const valid = files.filter(f => {
      const ext = '.' + f.name.split('.').pop()?.toLowerCase();
      return allowed.includes(ext) && f.size <= maxSize;
    });
    if (valid.length < files.length) toast.error('Algunos archivos no son válidos o superan 50MB y fueron omitidos');
    setSelectedFiles(prev => [...prev, ...valid]);
    e.target.value = '';
  };

  const getFileType = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
    const docExts = ['pdf', 'doc', 'docx'];
    const archiveExts = ['zip', 'rar'];

    if (imageExts.includes(ext || '')) return 'image';
    if (docExts.includes(ext || '')) return 'document';
    if (archiveExts.includes(ext || '')) return 'archive';
    return 'file';
  };

  const uploadFile = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${profile!.id}/${Date.now()}_${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from('message-attachments')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('message-attachments')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedFiles.length) || !selectedConversation || !profile) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    const filesToSend = [...selectedFiles];
    setSelectedFiles([]);
    setUploading(true);

    try {
      const firstFile = filesToSend[0] || null;
      let attachmentUrl: string | null = null;
      let attachmentType: string | null = null;

      if (firstFile) {
        attachmentUrl = await uploadFile(firstFile);
        attachmentType = getFileType(firstFile.name);
      }

      const { data, error } = await supabase
        .from('direct_messages')
        .insert({
          conversation_id: selectedConversation.id,
          sender_id: profile.id,
          receiver_id: selectedConversation.other_user.id,
          content: messageContent || '',
          attachment_url: attachmentUrl,
          attachment_type: attachmentType,
        })
        .select()
        .single();

      if (error) throw error;
      setMessages((prev) => [...prev, { ...data, sender: profile }]);

      for (const file of filesToSend.slice(1)) {
        const url = await uploadFile(file);
        const type = getFileType(file.name);
        const { data: d2, error: e2 } = await supabase
          .from('direct_messages')
          .insert({
            conversation_id: selectedConversation.id,
            sender_id: profile.id,
            receiver_id: selectedConversation.other_user.id,
            content: '',
            attachment_url: url,
            attachment_type: type,
          })
          .select()
          .single();
        if (!e2 && d2) setMessages((prev) => [...prev, { ...d2, sender: profile }]);
      }

      loadConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      setNewMessage(messageContent);
    } finally {
      setUploading(false);
    }
  };

  const sendBroadcast = async () => {
    if (!profile || !broadcastMessage.trim() || broadcastRecipients.size === 0) return;
    setUploading(true);
    try {
      let sentCount = 0;
      for (const recipientId of broadcastRecipients) {
        const u1 = profile.id < recipientId ? profile.id : recipientId;
        const u2 = profile.id < recipientId ? recipientId : profile.id;

        let convId: string;
        const { data: existing } = await supabase
          .from('conversations')
          .select('id')
          .eq('user1_id', u1)
          .eq('user2_id', u2)
          .maybeSingle();

        if (existing) {
          convId = existing.id;
        } else {
          const { data: newConv, error } = await supabase
            .from('conversations')
            .insert({ user1_id: u1, user2_id: u2 })
            .select('id')
            .single();
          if (error) continue;
          convId = newConv.id;
        }

        await supabase.from('direct_messages').insert({
          conversation_id: convId,
          sender_id: profile.id,
          receiver_id: recipientId,
          content: broadcastMessage,
        });
        sentCount++;
      }
      loadConversations();
      setShowNewChatModal(false);
      setModalView('menu');
      setBroadcastRecipients(new Set());
      setBroadcastMessage('');
      toast.success(`Mensaje enviado a ${sentCount} alumno(s)`);
    } catch (error) {
      console.error('Error sending broadcast:', error);
      toast.error('Error al enviar el mensaje');
    } finally {
      setUploading(false);
    }
  };

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  const escapeHtml = (s: string) =>
    s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const highlightText = (text: string, query: string) => {
    const safeText = escapeHtml(text);
    if (!query.trim()) return safeText;

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = safeText.split(new RegExp(`(${escapeHtml(escapedQuery)})`, 'gi'));
    return parts
      .map((part) =>
        part.toLowerCase() === escapeHtml(query).toLowerCase()
          ? `<mark class="bg-cubus-lime text-cubus-bg px-1 rounded">${part}</mark>`
          : part,
      )
      .join('');
  };

  const filteredConversations = !selectedConversation && searchQuery.trim()
    ? conversations.filter(conv =>
        conv.other_user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.other_user.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : conversations;

  const filteredAvailableUsers = !selectedConversation && searchQuery.trim()
    ? availableUsers.filter(user =>
        !conversations.some(conv => conv.other_user.id === user.id) &&
        (user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
         user.email.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : availableUsers.filter(user => !conversations.some(conv => conv.other_user.id === user.id));

  const filteredMessages = selectedConversation && searchQuery.trim()
    ? messages.filter(msg =>
        msg.content?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : messages;

  const conversationsWithUnread = conversations.filter(c => c.unread_count > 0);

  if (loading) {
    return (
      <div className="flex flex-col flex-1 p-4 sm:p-6 lg:p-8">
        <div className="mb-6">
          <h1 className="text-display text-3xl font-bold text-white mb-2">Mensajes</h1>
          <p className="text-muted">Cargando conversaciones…</p>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden p-4 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6 flex-shrink-0">
        <div>
          <h1 className="text-display text-3xl font-bold text-white mb-1">Mensajes</h1>
          <p className="text-muted">
            Conversaciones con {profile?.role === 'alumno' ? 'docentes' : 'alumnos'}
          </p>
        </div>

        <div className="relative" ref={notificationRef}>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className={`relative p-3 rounded-lg transition-all ${
              totalUnreadCount > 0
                ? 'bg-cubus-lime text-cubus-bg hover:bg-cubus-lime-dim'
                : 'glass-surface text-muted hover:bg-white/[0.06]'
            }`}
          >
            <Bell className={`w-6 h-6 ${totalUnreadCount > 0 ? 'animate-pulse' : ''}`} />
            {totalUnreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-80 glass-surface border border-white/[0.1] rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
              <div className="p-4 border-b border-white/[0.1] flex items-center justify-between">
                <h3 className="font-bold text-white">Notificaciones</h3>
                <button
                  onClick={() => setShowNotifications(false)}
                  className="text-muted hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {conversationsWithUnread.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-faint mx-auto mb-3" />
                  <p className="text-muted">No hay mensajes nuevos</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-600">
                  {conversationsWithUnread.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => {
                        setSelectedConversation(conv);
                        setShowNotifications(false);
                      }}
                      className="w-full p-4 hover:bg-white/[0.04] transition-all text-left"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-cubus-lime/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-cubus-lime font-bold">
                            {conv.other_user.full_name?.charAt(0) || conv.other_user.email.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-white truncate">
                              {conv.other_user.full_name || conv.other_user.email}
                            </p>
                            <span className="w-5 h-5 rounded-full bg-cubus-lime text-cubus-bg flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {conv.unread_count}
                            </span>
                          </div>
                          <p className="text-sm text-muted truncate">
                            {conv.last_message || 'Mensaje nuevo'}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-subtle" />
          <input
            type="text"
            placeholder={selectedConversation ? "Buscar en conversación..." : "Buscar conversación..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 glass-surface border border-white/[0.08] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cubus-lime"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-subtle hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowNewChatModal(true)}
          className="px-6 py-2.5 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg rounded-lg font-medium transition-all whitespace-nowrap"
        >
          Nuevo chat
        </button>
      </div>

      <div className="glass-surface rounded-xl border border-white/[0.06] flex-1 flex overflow-hidden min-h-0 max-h-full">
        <div className="w-1/3 border-r border-white/[0.1] flex flex-col overflow-hidden min-h-0 max-h-full">
          <div className="flex-1 overflow-y-auto min-h-0">
            {filteredConversations.length === 0 && availableUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-4">
                <MessageSquare className="w-12 h-12 text-faint mb-3" />
                <p className="text-muted text-center">No hay usuarios disponibles</p>
                <p className="text-subtle text-sm text-center mt-1">
                  {profile?.role === 'alumno'
                    ? 'Debes estar inscrito en un curso para chatear con docentes'
                    : 'Debes tener alumnos inscritos en tus cursos'}
                </p>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-4 space-y-3">
                <div className="text-center py-4">
                  <MessageSquare className="w-10 h-10 text-faint mx-auto mb-2" />
                  <p className="text-muted text-sm">No hay conversaciones activas</p>
                  <p className="text-subtle text-xs mt-1">
                    Haz clic en un {profile?.role === 'alumno' ? 'docente' : 'alumno'} para iniciar
                  </p>
                </div>
                <div className="border-t border-white/[0.08] pt-3">
                  <p className="text-muted text-sm font-medium mb-2 px-2">
                    {profile?.role === 'alumno' ? 'Docentes disponibles' : 'Alumnos disponibles'}
                  </p>
                  {availableUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => startNewConversation(user)}
                      className="w-full p-3 mb-2 rounded-lg glass-deep hover:bg-white/[0.06] border border-white/[0.08] hover:border-cubus-lime/40 transition-all text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-cubus-lime/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-cubus-lime font-bold">
                            {user.full_name?.charAt(0) || user.email.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">
                            {user.full_name || user.email}
                          </p>
                          <p className="text-xs text-muted capitalize">{user.role}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => setSelectedConversation(conv)}
                  className={`w-full p-4 border-b border-white/[0.08] hover:bg-white/[0.04] transition-all text-left ${
                    selectedConversation?.id === conv.id ? 'glass-deep' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-cubus-lime/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-cubus-lime font-bold text-lg">
                        {conv.other_user.full_name?.charAt(0) || conv.other_user.email.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-white truncate">
                          {conv.other_user.full_name || conv.other_user.email}
                        </h3>
                        {conv.unread_count > 0 && (
                          <div className="w-6 h-6 rounded-full bg-cubus-lime text-cubus-bg flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {conv.unread_count}
                          </div>
                        )}
                      </div>
                      <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-white font-medium' : 'text-muted'}`}>
                        {conv.last_message || 'No hay mensajes'}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden min-h-0 max-h-full">
            {selectedConversation ? (
              <>
                <div className="p-4 border-b border-white/[0.1] glass-surface flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-cubus-lime/20 flex items-center justify-center">
                      <span className="text-cubus-lime font-bold">
                        {selectedConversation.other_user.full_name?.charAt(0) || selectedConversation.other_user.email.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-bold text-white">
                        {selectedConversation.other_user.full_name || selectedConversation.other_user.email}
                      </h3>
                      <p className="text-sm text-muted capitalize">
                        {selectedConversation.other_user.role}
                      </p>
                    </div>
                  </div>
                </div>

                <div ref={messagesContainerRef} className="flex-1 overflow-y-auto bg-black/30 min-h-0 max-h-full">
                  {searchQuery.trim() && filteredMessages.length === 0 ? (
                    <div className="flex items-center justify-center h-full p-4">
                      <div className="text-center">
                        <Search className="w-12 h-12 text-faint mx-auto mb-3" />
                        <p className="text-muted">No se encontraron mensajes</p>
                        <p className="text-subtle text-sm mt-1">
                          No hay resultados para "{searchQuery}"
                        </p>
                      </div>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full p-4">
                      <div className="text-center">
                        <MessageSquare className="w-12 h-12 text-faint mx-auto mb-3" />
                        <p className="text-muted">No hay mensajes</p>
                        <p className="text-subtle text-sm mt-1">Envía el primer mensaje</p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 space-y-3">
                      {searchQuery.trim() && (
                        <div className="sticky top-0 z-10 bg-cubus-lime/10 border border-cubus-lime/30 rounded-lg p-3 mb-4 backdrop-blur-sm">
                          <p className="text-sm text-cubus-lime text-center">
                            {filteredMessages.length} mensaje{filteredMessages.length !== 1 ? 's' : ''} encontrado{filteredMessages.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      )}
                      {filteredMessages.map((message) => {
                        const isOwn = message.sender_id === profile?.id;
                        return (
                          <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
                              <div
                                className={`rounded-2xl px-4 py-3 ${
                                  isOwn
                                    ? 'bg-cubus-lime text-cubus-bg'
                                    : 'glass-deep text-white border border-white/[0.08]'
                                }`}
                              >
                                {message.attachment_url && (
                                  <div className="mb-2">
                                    {message.attachment_type === 'image' ? (
                                      <img
                                        src={message.attachment_url}
                                        alt="Imagen adjunta"
                                        className="rounded-lg max-w-full h-auto max-h-64 object-contain cursor-pointer"
                                        onClick={() => window.open(message.attachment_url!, '_blank')}
                                      />
                                    ) : (
                                      <a
                                        href={message.attachment_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-3 py-2 bg-black/20 rounded-lg hover:bg-black/30 transition-colors"
                                      >
                                        <Paperclip className="w-4 h-4" />
                                        <span className="text-sm font-medium">
                                          {message.attachment_url.split('/').pop()?.split('_').slice(1).join('_') || 'Archivo adjunto'}
                                        </span>
                                      </a>
                                    )}
                                  </div>
                                )}
                                {message.content && (
                                  searchQuery.trim() ? (
                                    <p
                                      className="text-sm whitespace-pre-wrap break-words"
                                      dangerouslySetInnerHTML={{ __html: highlightText(message.content, searchQuery) }}
                                    />
                                  ) : (
                                    <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                                  )
                                )}
                              </div>
                              <div className="px-1 mt-1 flex items-center gap-1">
                                <span className="text-xs text-subtle">
                                  {new Date(message.created_at).toLocaleTimeString('es-ES', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </span>
                                {isOwn && (
                                  <span className="text-subtle">
                                    {message.read ? (
                                      <CheckCheck className="w-3 h-3 text-cubus-lime" />
                                    ) : (
                                      <Check className="w-3 h-3" />
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                <form onSubmit={sendMessage} className="p-4 border-t border-white/[0.1] glass-surface flex-shrink-0">
                  {selectedFiles.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {selectedFiles.map((file, i) => (
                        <div key={i} className="flex items-center gap-1.5 p-1.5 pr-2 glass-deep border border-white/[0.08] rounded-lg max-w-[200px]">
                          <Paperclip className="w-4 h-4 text-cubus-lime flex-shrink-0" />
                          <span className="text-xs text-white truncate flex-1">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => setSelectedFiles(prev => prev.filter((_, j) => j !== i))}
                            className="text-muted hover:text-white transition-colors flex-shrink-0"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 items-center">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".png,.pdf,.rvt,.rfa,.rte,.jpeg,.jpg,.dwg,.docx,.xlsx,.pptx,.zip,.rar"
                      multiple
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-3 py-3 glass-deep hover:bg-white/[0.06] text-muted hover:text-white border border-white/[0.08] rounded-lg transition-all duration-200"
                      title="Adjuntar archivo"
                    >
                      <Paperclip className="w-5 h-5" />
                    </button>
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Escribe un mensaje..."
                      className="flex-1 px-4 py-3 glass-deep border border-white/[0.08] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cubus-lime"
                    />
                    <button
                      type="submit"
                      disabled={(!newMessage.trim() && !selectedFiles.length) || uploading}
                      className="px-6 py-3 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {uploading ? (
                        <div className="w-5 h-5 border-2 border-white/[0.08] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 text-faint mx-auto mb-4" />
                  <p className="text-muted text-lg">Selecciona una conversación</p>
                  <p className="text-subtle text-sm mt-1">
                    Elige un chat de la lista o inicia uno nuevo
                  </p>
                </div>
              </div>
            )}
          </div>
      </div>

      {showNewChatModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="glass-surface rounded-xl max-w-md w-full border border-white/[0.08]">
            <div className="p-6 border-b border-white/[0.08] flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">
                {modalView === 'menu' && 'Nueva conversación'}
                {modalView === 'individual' && 'Mensaje individual'}
                {modalView === 'group' && 'Mensaje grupal'}
                {modalView === 'broadcast' && 'Enviar a seleccionados'}
              </h3>
              <button
                onClick={() => {
                  setShowNewChatModal(false);
                  setModalView('menu');
                }}
                className="text-muted hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 max-h-[500px] overflow-y-auto">
              {profile?.role === 'docente' && modalView === 'menu' && (
                <div className="space-y-3">
                  <button
                    onClick={() => setModalView('individual')}
                    className="w-full p-4 rounded-lg glass-deep hover:bg-white/[0.06] border border-white/[0.08] hover:border-cubus-lime/40 transition-all text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-cubus-lime/20 flex items-center justify-center">
                        <MessageSquare className="w-6 h-6 text-cubus-lime" />
                      </div>
                      <div>
                        <p className="font-medium text-white text-lg">Mensaje individual</p>
                        <p className="text-sm text-muted">Enviar mensaje a un alumno específico</p>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setModalView('group')}
                    className="w-full p-4 rounded-lg glass-deep hover:bg-white/[0.06] border border-white/[0.08] hover:border-cubus-lime/40 transition-all text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-cubus-lime/20 flex items-center justify-center">
                        <Users className="w-6 h-6 text-cubus-lime" />
                      </div>
                      <div>
                        <p className="font-medium text-white text-lg">Mensaje grupal</p>
                        <p className="text-sm text-muted">Enviar mensaje a todos los alumnos de un curso</p>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setModalView('broadcast')}
                    className="w-full p-4 rounded-lg glass-deep hover:bg-white/[0.06] border border-white/[0.08] hover:border-cubus-lime/40 transition-all text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-cubus-lime/20 flex items-center justify-center">
                        <MessageSquare className="w-6 h-6 text-cubus-lime" />
                      </div>
                      <div>
                        <p className="font-medium text-white text-lg">Enviar a seleccionados</p>
                        <p className="text-sm text-muted">Elegir alumnos específicos y enviar el mensaje una vez</p>
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {(modalView === 'individual' || (profile?.role === 'alumno' && modalView === 'menu')) && (
                <>
                  {modalView === 'individual' && profile?.role === 'docente' && (
                    <button
                      onClick={() => setModalView('menu')}
                      className="mb-4 text-sm text-muted hover:text-white transition-colors flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Volver
                    </button>
                  )}
                  {availableUsers.length === 0 ? (
                    <p className="text-muted text-center py-8">
                      No hay usuarios disponibles
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {availableUsers.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => startNewConversation(user)}
                          className="w-full p-3 rounded-lg glass-deep hover:bg-white/[0.06] border border-white/[0.08] hover:border-cubus-lime/40 transition-all text-left"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-cubus-lime/20 flex items-center justify-center">
                              <span className="text-cubus-lime font-bold">
                                {user.full_name?.charAt(0) || user.email.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-white">
                                {user.full_name || user.email}
                              </p>
                              <p className="text-sm text-muted capitalize">{user.role}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {modalView === 'group' && (
                <GroupMessageForm
                  courses={teacherCourses}
                  onSend={sendMessageToAllStudents}
                  onCancel={() => setModalView('menu')}
                />
              )}

              {modalView === 'broadcast' && (
                <>
                  <button
                    onClick={() => setModalView('menu')}
                    className="mb-4 text-sm text-muted hover:text-white transition-colors flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Volver
                  </button>
                  {availableUsers.length === 0 ? (
                    <p className="text-muted text-center py-8">No hay alumnos disponibles</p>
                  ) : (
                    <>
                      <p className="text-sm text-muted mb-3">
                        {broadcastRecipients.size > 0
                          ? `${broadcastRecipients.size} alumno(s) seleccionado(s)`
                          : 'Selecciona los destinatarios'}
                      </p>
                      <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                        {availableUsers.map((user) => {
                          const checked = broadcastRecipients.has(user.id);
                          return (
                            <label
                              key={user.id}
                              className={`flex items-center gap-3 p-3 rounded-lg glass-deep border cursor-pointer transition-all ${
                                checked ? 'border-cubus-lime/60 bg-cubus-lime/5' : 'border-white/[0.08] hover:border-white/20'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setBroadcastRecipients((prev) => {
                                    const next = new Set(prev);
                                    e.target.checked ? next.add(user.id) : next.delete(user.id);
                                    return next;
                                  });
                                }}
                                className="w-4 h-4 accent-cubus-lime flex-shrink-0"
                              />
                              <div className="w-8 h-8 rounded-full bg-cubus-lime/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-cubus-lime font-bold text-sm">
                                  {user.full_name?.charAt(0) || user.email.charAt(0)}
                                </span>
                              </div>
                              <span className="font-medium text-white text-sm">{user.full_name || user.email}</span>
                            </label>
                          );
                        })}
                      </div>
                      <textarea
                        value={broadcastMessage}
                        onChange={(e) => setBroadcastMessage(e.target.value)}
                        placeholder="Escribe el mensaje..."
                        rows={3}
                        className="w-full px-4 py-3 glass-deep border border-white/[0.08] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cubus-lime resize-none mb-4"
                      />
                      <button
                        onClick={sendBroadcast}
                        disabled={broadcastRecipients.size === 0 || !broadcastMessage.trim() || uploading}
                        className="w-full py-3 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {uploading ? 'Enviando...' : `Enviar a ${broadcastRecipients.size || '...'} alumno(s)`}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
